// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2023-04-26 18:40:43 +0200 (Wed, 26 Apr 2023) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 134161 $
//  Purpose   : viewer mouse/touch stuff
// =============================================================================
// ---Third-party imports
import CursorPos from 'react-cursor-position'

// ---Framework imports
import {callIfFunc, isArray, isNil} from 'ldash'

import log from 'uix/logging'

// ---Globals

// copied from react-cursor-position/utils
export const addEventListener = (node, eventName, handler, options) => {
  node.addEventListener(eventName, handler, options)
  return {
    removeEventListener: () => {
      node.removeEventListener(eventName, handler, options)
    },
  }
}

const mkeys = ['shift', 'alt', 'ctrl']
export const isMetaKey = (e, selectMetaKey) => {
  if (isNil(selectMetaKey)) return true

  let keys = isArray(selectMetaKey) ? selectMetaKey : [selectMetaKey]
  for (let kcode of keys) {
    // null -> no meta keys should be active
    if (kcode === null && !mkeys.some(k => e[`${k}Key`])) return true
    // like e.shiftKey or e.altKey
    if (!!e[`${kcode}Key`]) return true
  }
  return false
}

// feed cursor width/height info via autosizer
// and connect mouse buttons
export class CursorPosition extends CursorPos {
  constructor(props) {
    super(props)
    this.state = {
      ...this.state,
      isDragging: false,
      isSelecting: false,
      ignoreNextOnClick: false,
      selectOrigin: {x: 0, y: 0},
    }
  }

  getDocumentRelativeElementOffset(el) {
    // patched our width/height understanding from virtualized autosizer
    let res = CursorPos.prototype.getDocumentRelativeElementOffset.call(this, el)
    let {width, height} = this.props
    res.w = width
    res.h = height
    return res
  }

  addEventListeners() {
    // maybe in future releases: https://github.com/ethanselzer/react-cursor-position/issues/20
    CursorPos.prototype.addEventListeners.call(this)
    if (this.props.onDrag || this.props.onSelect) {
      this.eventListeners.push(
        addEventListener(this.el, 'mousedown', this.onMouseDown),
        addEventListener(this.el, 'mouseup', this.onMouseUp),
        addEventListener(this.el, 'click', this.onClick),

        //~ addEventListener(this.el, 'touchstart', this.onTouchStart),
        addEventListener(this.el, 'touchend', this.onTouchEnd),
        addEventListener(this.el, 'touchmove', this.onTouchMove)
      )
    }
    if (this.props.onWheel) {
      // passive: false is important - we dont want to scroll the outer areas (by using preventDefault)
      this.eventListeners.push(addEventListener(this.el, 'wheel', this.onWheel, {passive: false}))
    }
  }

  startDrag = (x, y) => {
    this.setState({isDragging: true})
    // we update dragX/Y inplace below
    this.dragX = x
    this.dragY = y
    callIfFunc(this.props.onDragStart)
  }

  //~ onTouchStart = e => {
  //~ log.x('start', e)
  //~ }
  onTouchEnd = e => {
    this.onMouseUp(e)
  }

  onTouchMove = e => {
    let {isDragging} = this.state
    let {clientX, clientY} = e.touches[0]
    let rect = this.el.getBoundingClientRect()
    let [x, y] = [clientX - rect.left, clientY - rect.top]
    if (!isDragging) {
      this.startDrag(x, y)
    } else {
      this.dragMove(x, y)
    }
    e.preventDefault()
  }

  onMouseDown = e => {
    let isSelectionStart = isMetaKey(e, this.props.selectMetaKey)
    let isDragScrollStart = isMetaKey(e, this.props.dragScrollMetaKey)
    this.setState({ignoreNextOnClick: false})

    if (isDragScrollStart) {
      let {position} = this.state
      this.startDrag(position.x, position.y)
    } else if (isSelectionStart) {
      this.setState({isSelecting: true, selectOrigin: this.state.position})
      if (this.props.onSelect) {
        this.props.onSelect('start', this.state.position)
      }
    }
    e.preventDefault()
  }

  onMouseUp = e => {
    this._deactivate({shiftKey: !!e.shiftKey, altKey: !!e.altKey, ctrlKey: !!e.ctrlKey})
  }

  onClick = e => {
    if (this.state.ignoreNextOnClick) {
      this.setState({ignoreNextOnClick: false})
      //~ e.preventDefault()
      // prevent onClick handlers of area elements
      e.stopPropagation()
    }
  }

  onWheel = e => {
    if (e.ctrlKey) {
      // browser zoom
      return
    }

    if (!this.props.onWheel) {
      // only relevant on dynamic switch of `option.mouse.wheelZoom`
      return
    }
    let handled = this.props.onWheel(e.deltaY, e)
    if (handled) {
      e.preventDefault()
    }
  }

  _deactivate({shiftKey, altKey, ctrlKey}) {
    if (this.state.isDragging) {
      this.setState({isDragging: false})
      callIfFunc(this.props.onDragEnd)
    }

    if (this.state.isSelecting && this.props.onSelect) {
      this.setState({isSelecting: false})
      this.props.onSelect('end', {shiftKey, altKey, ctrlKey})
    }
  }

  deactivate() {
    CursorPos.prototype.deactivate.call(this)
    this._deactivate({})
    callIfFunc(this.props.onCursorDeactivate)
  }

  dragMove = (x, y) => {
    let {onDrag} = this.props

    if (onDrag) {
      // update this.dragX/Y inplace
      let deltaX = this.dragX - (this.dragX = x)
      let deltaY = this.dragY - (this.dragY = y)

      //XXX: maybe also move onDragStart down here
      if (!this.state.ignoreNextOnClick && Math.abs(deltaX) + Math.abs(deltaY) > 0) {
        this.setState({ignoreNextOnClick: true})
        // maybe use later
        //~ this.props.onDragStart()
      }

      onDrag(deltaX, deltaY)
    }
  }

  setPositionState(position) {
    CursorPos.prototype.setPositionState.call(this, position)
    let {isDragging, isSelecting, selectOrigin} = this.state
    let {onSelect} = this.props

    if (isDragging) {
      this.dragMove(position.x, position.y)
    }

    if (isSelecting && onSelect) {
      onSelect('resize', {
        x: selectOrigin.x,
        y: selectOrigin.y,
        width: position.x - selectOrigin.x,
        height: position.y - selectOrigin.y,
      })
    }
  }
}
